/* eslint-disable react-hooks/exhaustive-deps */
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { API_ENDPOINTS } from "../../constants/api";
import useAxios from "../../hooks/useAxios";
import {
    saveCampaign,
    setMessage,
    setStep
} from "../../redux/modules/compaigns/compaignSlice";
import { RootState } from "../../redux/rootReducer";
import { decodeMilliseconds, formatDateToYYYYMMDDTHHMM, getNowInTimezone, utcToZonedString, zonedLocalToUtcString, computeNextRecurrenceDate } from "../../utils/helper";
import { Option } from "../../utils/interface";
import useList from "../../hooks/useList";
import useSegment from "../../hooks/useSegment";
import { toaster } from "../../utils/toast";
import { CampaignType, RecurrenceEndMode, RecurrenceFrequency, RecurrencePattern } from "../../utils/enums";

const useCampaign = () => {
    const dispatch = useDispatch();
    const { step, campaignId, loading, error, campaignEditData } = useSelector(
        (state: RootState) => state.campaigns
    );
    const { orgLocations, selectedLocationId } = useSelector(
        (state: RootState) => state.user
    );
    const activeLocation = (orgLocations ?? []).find(
        (loc: any) => String(loc.id) === String(selectedLocationId)
    ) || (orgLocations && orgLocations.length > 0 ? orgLocations[0] : null);
    const activeTimezone = activeLocation?.location_timezone || 'UTC';

    const isExistingDraft = Boolean(campaignEditData?.id);
    const savedTimezone = campaignEditData?.recurrence_timezone;
    const { listMap } = useList({refresh: step === 2})
    const { segmentMap } = useSegment({refresh: step === 2})
    const [domains, setDomains] = useState<Option[]>([]);
    const [templates, setTemplates] = useState<any>({});
    const { postData, fetchData } = useAxios();
    const [templateWithFolderList, setTemplateWithFolderList] = useState<any>([])
    const [campaignPreviewHtml, setCampaignPreviewHtml] = useState<string>("");
    const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
    const { value, unit } = decodeMilliseconds(campaignEditData?.time_delay);
    const [activeNumbers, setActiveNumbers] = useState<any[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [formik2FormData, setFormik2FormData] = useState<any>({});

    useEffect(() => {
        if(error) {
          toaster({message: error, success: false})
          setCampaignPreviewLoading(false);
        }
    }, [error]);

    useEffect(() => {
        if (step === 2) {
            fetchDomains();
            getActiveNumbers();
        }
        if (step === 3) {
            fetchTemplates();
            getTemplateWithFolderList();
        }
        if (step === 4 && formik3.values.templateId) {
            fetchCampaignTemplatePreview(formik3.values.templateId);
        }
    }, [step]);

    const fetchCampaignTemplatePreview = async (templateId: string | number) => {
        try {
            setCampaignPreviewHtml("");
            setCampaignPreviewLoading(true);
            const response = await postData(API_ENDPOINTS.CAMPAIGN_TEMPLATE_PREVIEW, { templateId });
            if (!response?.success) {
                toaster({ message: response?.message || 'Unable to generate template preview', success: false });
                return;
            }
            setCampaignPreviewHtml(response.data?.html || "");
        } catch (error) {
            toaster({ message: 'Unable to generate template preview', success: false });
        } finally {
            setCampaignPreviewLoading(false);
        }
    };

    const validationSchema = Yup.object().shape({
        ...(step === 1 && {
            type: Yup.string().required("Type is required"),
            title: Yup.string().trim().required('Title is required').test(
                (value) => value.trim() !== ''
              ),
        }),
        ...(step === 2 && {
            toListType: Yup.string().required("To List Type is required"),
            toListId: Yup.array().min(1, 'Select at least one option'),
            scheduleTime: Yup.string().test({
                test: function (value) {
                    if (this.parent.isRecurring) return true;
                    return !!value || this.createError({ message: "Schedule Time is required" });
                }
            }),
            limit: Yup.string().test({
                test: function(value) {
                    if (this.parent.bulkEmailsLimitType === 2 && !this.parent.isRecurring) {
                        return !!value || this.createError({ message: "Limit is required" });
                    }
                    return true;
                }
            }),
            delayUnit: Yup.string().test({
                test: function(value) {
                    if (this.parent.bulkEmailsLimitType === 2 && !this.parent.isRecurring) {
                        return !!value || this.createError({ message: "Time Delay Type is required" });
                    }
                    return true;
                }
            }),
            delayValue: Yup.string().test({
                test: function(value) {
                    if (this.parent.bulkEmailsLimitType === 2 && !this.parent.isRecurring) {
                        return !!value || this.createError({ message: "Delay Value is required" });
                    }
                    return true;
                }
            }),
            recurrenceFrequency: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring) return true;
                    return !!value || this.createError({ message: "Frequency is required" });
                }
            }),
            recurrenceDayOfWeek: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring) return true;
                    const needsDayOfWeek = this.parent.recurrenceFrequency === RecurrenceFrequency.WEEKLY
                        || this.parent.recurrencePattern === RecurrencePattern.NTH_WEEKDAY;
                    if (!needsDayOfWeek) return true;
                    return (value !== "" && value !== undefined) || this.createError({ message: "Day of week is required" });
                }
            }),
            recurrencePattern: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring || this.parent.recurrenceFrequency !== RecurrenceFrequency.MONTHLY) return true;
                    return !!value || this.createError({ message: "Monthly pattern is required" });
                }
            }),
            recurrenceDayOfMonth: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring || this.parent.recurrencePattern !== RecurrencePattern.DAY_OF_MONTH) return true;
                    return !!value || this.createError({ message: "Day of month is required" });
                }
            }),
            recurrenceWeekOfMonth: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring || this.parent.recurrencePattern !== RecurrencePattern.NTH_WEEKDAY) return true;
                    return !!value || this.createError({ message: "Occurrence is required" });
                }
            }),
            recurrenceTime: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring) return true;
                    return !!value || this.createError({ message: "Send time is required" });
                }
            }),

            // Reccurance time zone validation code below(commented as temp disabled selection )
            // recurrenceTimezone: Yup.string().test({
            //     test: function (value) {
            //         if (!this.parent.isRecurring) return true;
            //         return !!value || this.createError({ message: "Timezone is required" });
            //     }
            // }),
            recurrenceEndDate: Yup.string().test({
                test: function (value) {
                    if (!this.parent.isRecurring || this.parent.recurrenceEndMode !== RecurrenceEndMode.END_DATE) return true;
                    return !!value || this.createError({ message: "End date is required" });
                }
            }),
        }),
        ...(step === 3 && {
            templateId: Yup.string().required("Template is required"),
        }),
    });

    const formik1 = useFormik({
        initialValues: {
            title: campaignEditData?.title ?? "",
            type: campaignEditData?.type ?? "",
            isCreateCampaign: true
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            handleSubmit(values);
        },
    });

    const formik2 = useFormik({
        initialValues: {
            toListType: campaignEditData?.to_list_type ?? "",
            toListId: [],
            fromEmailDomain: campaignEditData?.from_email_domain ?? "",
            fromSmsNumber: campaignEditData?.number_id ?? "",
            scheduleTime: isExistingDraft
                ? (savedTimezone && campaignEditData?.scheduled_time ? utcToZonedString(campaignEditData.scheduled_time, savedTimezone) : "")
                : getNowInTimezone(activeTimezone),
            limit: campaignEditData?.limit ?? "",
            delayUnit: campaignEditData?.time_delay ? unit : "",
            delayValue: campaignEditData?.time_delay ? value : "",
            bulkEmailsLimitType: campaignEditData?.limit ? 2 : 1,
            isRecurring: campaignEditData?.is_recurring ?? false,
            recurrenceFrequency: campaignEditData?.recurrence_frequency ?? "",
            recurrencePattern: campaignEditData?.recurrence_pattern ?? "",
            recurrenceDayOfWeek: campaignEditData?.recurrence_day_of_week ?? "",
            recurrenceDayOfMonth: campaignEditData?.recurrence_day_of_month ?? "",
            recurrenceWeekOfMonth: campaignEditData?.recurrence_week_of_month ?? "",
            recurrenceTime: campaignEditData?.recurrence_time ?? "09:00",
            recurrenceTimezone: isExistingDraft
                ? (savedTimezone ?? "")
                : activeTimezone,
            recurrenceEndMode: campaignEditData?.recurrence_end_mode ?? RecurrenceEndMode.INDEFINITE,
            recurrenceEndDate: isExistingDraft
                ? (savedTimezone && campaignEditData?.recurrence_end_date ? utcToZonedString(campaignEditData.recurrence_end_date, savedTimezone) : "")
                : "",
        },
        validationSchema: Yup.object().shape({
            ...validationSchema.fields,
            // ...(formik1.values.type === CampaignType.EMAIL && Yup.object().shape({
            //     fromEmailDomain: Yup.string().required("From Email Domain is required"),
            // }).fields),
            // ...(formik1.values.type === CampaignType.SMS && Yup.object().shape({
            //     fromSmsNumber: Yup.string().required("From SMS Number is required"),
            // }).fields),
        }),
        onSubmit: async (values: any) => {
            if (values.isRecurring) {
                values.limit = ""
                values.delayUnit = ""
                values.delayValue = ""

                // COMMENTED: only used today's date, didn't account for day-of-week/month or already-passed times
                // if (!values.scheduleTime && values.recurrenceTime) {
                //     const [hours, minutes] = values.recurrenceTime.split(':');
                //     const now = new Date();
                //     now.setHours(Number(hours), Number(minutes), 0, 0);
                //     values.scheduleTime = formatDateToYYYYMMDDTHHMM(now);
                // } else {
                //     values.scheduleTime = values.scheduleTime || formatDateToYYYYMMDDTHHMM();
                // }

                // NEW: computes the true next occurrence, factoring in day-of-week/month, pattern, and whether today's slot already passed
                if (!values.scheduleTime) {
                    values.scheduleTime = formatDateToYYYYMMDDTHHMM(computeNextRecurrenceDate({
                        ...values,
                        recurrenceTimezone: values.recurrenceTimezone || activeTimezone,
                    }));
                }

                if (values.recurrenceEndMode !== RecurrenceEndMode.END_DATE) {
                    values.recurrenceEndDate = "";
                }
            } else if (values.bulkEmailsLimitType === 1) {
                values.limit = ""
                values.delayUnit = ""
                values.delayValue = ""
            }
            handleSubmit(values);
        },
    });

    const formik3 = useFormik({
        initialValues: {
            subject: campaignEditData?.subject ?? "",
            templateId: campaignEditData?.template_id ?? "",
        },
        validationSchema: Yup.object().shape({
            ...validationSchema.fields,
            ...(formik1.values.type === CampaignType.EMAIL && Yup.object().shape({
                subject: Yup.string().trim().required("Subject is required"),
            }).fields),
        }),
        onSubmit: async (values) => {
            handleSubmit(values);
        },
    });

    // useEffect(() => {
    //     if (+(campaignEditData?.to_list_type??0) === 1) {
    //         const listIds = campaignEditData?.to_list_id.map((v: number)=>listMap[v])
    //         formik2.setFieldValue('toListId', listIds)
    //     }else{
    //         const listIds = campaignEditData?.to_list_id.map((v: number)=>segmentMap[v])
    //         formik2.setFieldValue('toListId', listIds)
    //     }
    // }, [campaignEditData?.to_list_type, listMap, segmentMap, step]);

    useEffect(() => {
        if(campaignEditData?.to_list_type && formik2.values.toListId.length === 0){
            const map = +(campaignEditData?.to_list_type ?? 0) === 1 ? listMap : segmentMap;
            const ids = (campaignEditData?.to_list_id || []).map(id => map[id]).filter(id => id !== undefined);
            formik2.setFieldValue('toListId', ids);
        }
    }, [campaignEditData?.to_list_type, listMap, segmentMap, step]);

    //initial value set
    useEffect(() => {
        formik2.setFieldValue('templateId', campaignEditData?.template_id);
        formik2.setFieldValue('subject', campaignEditData?.subject);
        formik2.setFieldValue('fromEmailDomain', campaignEditData?.from_email_domain);
    }, [campaignEditData?.template_id, campaignEditData?.subject, campaignEditData?.from_email_domain]);

    const fetchDomains = async () => {
        try {
            const response = await postData(API_ENDPOINTS.GET_ALL_DOMAINS, {status: true});
            const formattedOption = response?.data.map((v: any) => ({
                value: v.id,
                text: v.email,
                key: v.email,
            }));
            setDomains(formattedOption);
        } catch (error) {}
    }

    const fetchTemplates = async () => {
        try {
            const response = await postData(API_ENDPOINTS.GET_ALL_TEMPLATES, {});
            const formattedOption = response?.data?.templates.reduce((acc: any, v: any) => {
                acc[v.id] = {
                    value: v.id,
                    text: v.name,
                    key: v.name,
                    data: v,
                };
                return acc;
            }, {});
            setTemplates(formattedOption);
        } catch (error) {}
    }

    const getTemplateWithFolderList = async () => {
        try {
          const type = formik1?.values?.type;
          const response = await postData(API_ENDPOINTS.GET_ALL_TEMPLATE_WITH_FOLDER, { type });
          setTemplateWithFolderList(response.data.folders);
        } catch (error) {}
      }

    const handleClose = () => {
        dispatch(setStep(0));
    };

    const handleBack = () => {
        if (step === 2) {
            handleClose()
            dispatch(setMessage('Saved as draft'))
        }else{
            dispatch(setStep(step-1));
        }
    };

    const handleSubmit = (values: any) => {
        const selectedTz = values.recurrenceTimezone || activeTimezone;
        let payload = {
            ...values,
            step,
            campaignId,
            type: formik1.values.type,
            scheduleTime: values.scheduleTime ? zonedLocalToUtcString(values.scheduleTime, selectedTz) : "",
            recurrenceEndDate: values.recurrenceEndDate ? zonedLocalToUtcString(values.recurrenceEndDate, selectedTz) : "",
            recurrenceTimezone: selectedTz,
            locationId: localStorage.getItem("locationId"),
            isCreateCampaign: formik1.values.isCreateCampaign,
            title: formik1.values.title,
            recurrenceDayOfWeek: values.recurrenceDayOfWeek === "" ? null : values.recurrenceDayOfWeek,
            recurrenceDayOfMonth: values.recurrenceDayOfMonth === "" ? null : values.recurrenceDayOfMonth,
            ...(step === 2 && {
                toListId: formik2.values.toListId?.map((item: any) => item.id) ?? []
            })
        };
        const isRecurringSubmission = formik2.values.isRecurring;
        const nowInSelectedTz = getNowInTimezone(selectedTz);
        if (step === 2 && !isRecurringSubmission) {
            if (!values.scheduleTime) {
                toaster({message: "Please select schedule time", success: false});
                return;
            }
            if (values.scheduleTime < nowInSelectedTz) {
                toaster({message: "scheduled time can't be in past", success: false});
                return;
            }
        }

        if(step === 2) {
            if(values.fromEmailDomain || values.fromSmsNumber) {
                dispatch(saveCampaign(payload));
            } else{
                setFormik2FormData(payload)
                setShowConfirmationModal(true)
            }
        } else {
            if (step === 3) {
                if (activeLocation?.is_active === false) {
                    toaster({ message: "This location is deactivated. Campaign scheduling is blocked.", success: false });
                    return;
                }
                setCampaignPreviewLoading(true);
            }
            dispatch(saveCampaign(payload));
        }
    };

    const domainModalConfirmation = () => {
        dispatch(saveCampaign(formik2FormData));
    }

    const getActiveNumbers = async () => {
        try {
          const response = await fetchData(API_ENDPOINTS.GET_NUMBERS);
          const formattedOption = response?.data.map((v: any) => ({
            value: v.id,
            text: v.number,
            key: v.number,
        }));
          setActiveNumbers(formattedOption);
        } catch (error) {}
      };


    return {
        formik1,
        formik2,
        formik3,
        loading,
        domains,
        activeNumbers,
        templates,
        templateWithFolderList,
        step,
        handleClose,
        handleBack,
        handleSubmit,
        campaignId,
        campaignEditData,
        listMap,
        segmentMap,
        showConfirmationModal,
        setShowConfirmationModal,
        domainModalConfirmation,
        campaignType: campaignEditData?.type,
        campaignPreviewHtml,
        campaignPreviewLoading,
    };
};

export default useCampaign
